import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { CoverBanner } from "@/features/projects/CoverBanner";
// import { EvaluationsCard } from "@/features/projects/EvaluationsCard";  // pausado — ver EVALUATIONS_PAUSED.md
import { HealthEditor } from "@/features/projects/HealthEditor";
import { MonitorEditor } from "@/features/projects/MonitorEditor";
import { LinksManager } from "@/features/projects/LinksManager";
import { MembersManager } from "@/features/projects/MembersManager";
// ProjectAssessmentCard pausado — ver EVALUATIONS_PAUSED.md.
// import { ProjectAssessmentCard } from "@/features/projects/ProjectAssessmentCard";
// Import pausado junto con la card de KPIs del proyecto — ver EVALUATIONS_PAUSED.md.
// import { ProjectMetricsCard } from "@/features/projects/ProjectMetricsCard";
// import { CloseSurveysButton } from "@/features/projects/CloseSurveysButton";  // pausado — ver EVALUATIONS_PAUSED.md
// import { DocumentsManager } from "@/features/projects/DocumentsManager";
// (pausado — ver comentario abajo)
import { MilestonesCard } from "@/features/projects/MilestonesCard";
import { IssuesList } from "@/features/projects/IssuesList";
import { PlaneRefreshHeader, type RefreshMeta } from "@/components/PlaneRefreshHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import type { Project, ProjectLink, ProjectDocument, PlaneIssueSummary } from "@axorial/shared";

interface StandupMention {
  itemId: string;
  standupId: string;
  section: "yesterday" | "today";
  text: string | null;
  date: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  tickets: Array<{
    issueTitleSnapshot: string;
    issueUrl: string;
    projectIdentifier: string | null;
    sequenceId: number | null;
  }>;
}

interface ProjectDetail {
  project: Project;
  links: ProjectLink[];
  members: { userId: string; roleInProject: string; name: string; email: string }[];
  documents: ProjectDocument[];
  standupMentions: StandupMention[];
}

export const Route = createFileRoute("/projects/$projectId/")({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { projectId } = Route.useParams();

  const { data, isLoading } = useQuery<ProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: () => api.get(`/projects/${projectId}`),
  });

  const qc = useQueryClient();
  type IssuesResp = {
    issues: PlaneIssueSummary[];
    users: Record<string, { id: string; name: string; email: string }>;
    meta?: RefreshMeta;
    note?: string;
  };
  const issuesQuery = useQuery<IssuesResp>({
    queryKey: ["plane", "issues", projectId],
    queryFn: () => api.get(`/plane/projects/${projectId}/issues`),
    enabled: !!data?.project.planeProjectId,
  });
  const refreshIssues = useMutation({
    mutationFn: () => api.get<IssuesResp>(`/plane/projects/${projectId}/issues?refresh=1`),
    onSuccess: (fresh) => qc.setQueryData(["plane", "issues", projectId], fresh),
  });

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }
  // documents queda fuera del destructure mientras la sección está oculta.
  const { project, links, members, standupMentions } = data;

  return (
    <div className="space-y-6">
      <CoverBanner project={project} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-6">
          {project.description && (
            <Card title="Descripción">
              <p className="text-sm text-base-content/80 leading-relaxed whitespace-pre-wrap">
                {project.description}
              </p>
            </Card>
          )}

          <Card title="Milestones" badge={project.planeProjectId ? "Plane" : null}>
            <MilestonesCard projectId={projectId} hasPlaneLink={!!project.planeProjectId} canSync />
          </Card>

          <Card
            title="Issues en Plane"
            badge={
              project.planeProjectId && issuesQuery.data?.issues.length
                ? `${issuesQuery.data.issues.length} totales`
                : project.planeProjectId
                  ? "Sincronizado"
                  : null
            }
            action={
              project.planeProjectId && issuesQuery.data?.issues.length ? (
                <Link
                  to="/projects/$projectId/issues"
                  params={{ projectId }}
                  className="link link-primary text-xs no-underline hover:underline"
                >
                  Vista completa →
                </Link>
              ) : null
            }
          >
            {!project.planeProjectId ? (
              <EmptyState>
                Vincula este proyecto con Plane para ver sus issues aquí. Edita el proyecto y añade
                el <code className="font-mono text-xs">Plane project ID</code>.
              </EmptyState>
            ) : issuesQuery.isLoading ? (
              <div className="grid place-items-center py-8">
                <span className="loading loading-spinner text-primary" />
              </div>
            ) : (
              <>
                <PlaneRefreshHeader
                  meta={issuesQuery.data?.meta ?? null}
                  isPending={refreshIssues.isPending}
                  onRefresh={() => refreshIssues.mutate()}
                  className="mb-3"
                />
                <IssuesList
                  issues={issuesQuery.data?.issues ?? []}
                  users={issuesQuery.data?.users}
                  compact
                  projectIdForLink={projectId}
                />
              </>
            )}
          </Card>

          {standupMentions.length > 0 && (
            <Card title="Standups recientes del proyecto" badge={`${standupMentions.length} ítems`}>
              <StandupMentionsList mentions={standupMentions} />
            </Card>
          )}

          {/* TODO(documentos-proyecto): sección oculta para usuario final.
              No encontramos buena forma de usarla — los archivos
              financieros viven en Finanzas → Archivero. Decidir si se
              quita definitivamente o se rediseña; DocumentsManager y el
              endpoint /api/projects/:id/documents siguen vivos por si
              lo retomamos. Para reactivar: descomenta el import arriba
              y restaura la <Card title="Documentos">. */}
        </section>

        <aside className="space-y-6">
          <Card title="Salud del proyecto">
            <HealthEditor
              projectId={projectId}
              health={project.health}
              healthNote={project.healthNote}
            />
          </Card>

          <Card title="Uptime / monitor">
            <MonitorEditor projectId={projectId} />
          </Card>

          <Card title="Estado">
            <dl className="text-sm space-y-2">
              <Meta label="Status">
                <span className="capitalize">{project.status}</span>
              </Meta>
              <Meta label="Tipo">{project.type === "client" ? "Cliente" : "Interno"}</Meta>
              <Meta label="Iniciado">
                {project.startedAt ? formatDate(project.startedAt) : "—"}
              </Meta>
              <Meta label="Target">
                {project.targetEndAt ? formatDate(project.targetEndAt) : "—"}
              </Meta>
            </dl>
          </Card>

          {/* Evaluación compuesta (assessments) pausada — ver EVALUATIONS_PAUSED.md. */}
          {/* <Card title="Salud del proyecto (evaluación)">
            <ProjectAssessmentCard projectId={projectId} />
          </Card> */}

          {/* KPIs del proyecto pausados junto al resto del módulo de
              evaluaciones — ver EVALUATIONS_PAUSED.md. Se ocultan hasta
              decidir qué forma final tendrá la captura/visualización.
          <Card title="KPIs del proyecto">
            <ProjectMetricsCard projectId={projectId} />
          </Card>
          */}

          {/* Card "Cuestionarios" pausada — ver EVALUATIONS_PAUSED.md.
          <Card
            title="Cuestionarios"
            action={
              project.status === "completed" ? (
                <CloseSurveysButton projectId={projectId} />
              ) : null
            }
          >
            <EvaluationsCard projectId={projectId} />
          </Card>
          */}

          <Card title="Links">
            <LinksManager projectId={projectId} links={links} />
          </Card>

          <Card title="Equipo">
            <MembersManager
              projectId={projectId}
              members={members as Parameters<typeof MembersManager>[0]["members"]}
            />
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Card({
  title,
  badge,
  action,
  children,
}: {
  title: string;
  badge?: string | null;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card-elevated">
      <div className="px-5 pt-5 pb-2 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
          {title}
        </h3>
        <div className="flex items-center gap-2">
          {badge && <span className="badge badge-ghost badge-sm">{badge}</span>}
          {action}
        </div>
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function EmptyState({ children, compact }: { children: ReactNode; compact?: boolean }) {
  return (
    <p className={compact ? "text-sm text-base-content/55" : "text-sm text-base-content/60 py-2"}>
      {children}
    </p>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs uppercase tracking-wider text-base-content/55">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

const SECTION_LABEL: Record<"yesterday" | "today", string> = {
  yesterday: "Ayer",
  today: "Hoy",
};
const SECTION_BADGE: Record<"yesterday" | "today", string> = {
  yesterday: "badge-ghost",
  today: "badge-primary",
};

/** Lista compacta de los standup items recientes que mencionaron este
 * proyecto. Cada item linkea al detalle del standup. Si trae tickets
 * de Plane asociados, se muestran inline con su URL. */
function StandupMentionsList({ mentions }: { mentions: StandupMention[] }) {
  return (
    <ul className="divide-y divide-base-300/40 -mx-1">
      {mentions.map((m) => (
        <li key={m.itemId} className="px-1 py-2.5">
          <Link
            to="/standups/$standupId"
            params={{ standupId: m.standupId }}
            className="block hover:bg-base-200/40 -mx-1 px-1 py-1 rounded transition-colors"
          >
            <div className="flex items-center gap-2 flex-wrap text-[11px] mb-1">
              <span className={`badge badge-xs ${SECTION_BADGE[m.section]}`}>
                {SECTION_LABEL[m.section]}
              </span>
              <span className="text-base-content/55">{formatDate(m.date)}</span>
              <span className="text-base-content/45">·</span>
              <span className="text-base-content/70 font-medium">{m.authorName}</span>
            </div>
            {m.text && (
              <p className="text-sm text-base-content/85 line-clamp-2 leading-relaxed">{m.text}</p>
            )}
            {m.tickets.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 mt-1.5">
                {m.tickets.map((t, i) => {
                  const num =
                    t.projectIdentifier && t.sequenceId !== null
                      ? `${t.projectIdentifier}-${t.sequenceId}`
                      : null;
                  const label = num ? `${num} · ${t.issueTitleSnapshot}` : t.issueTitleSnapshot;
                  return (
                    <li key={i}>
                      <a
                        href={t.issueUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="badge badge-sm badge-ghost gap-1 hover:badge-primary"
                      >
                        🎫 <span className="truncate max-w-[18rem]">{label}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}
